import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApi } from '@/hooks/useApi';
import { Building2, User } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * STORY-STRIPE-009a — Add a second profile/subscription on the same Stripe
 * Customer. Used by:
 *
 *   variant="add_owner"      → individual or coach who wants to open a gym
 *   variant="add_individual" → owner or coach who wants to train personally
 *
 * On success, the user is redirected to a Stripe Checkout session for the
 * second subscription. The new profile lands as `pending` until the webhook
 * flips it to active on payment.
 */
export function AddProfileCard({ variant }: { variant: 'add_owner' | 'add_individual' }) {
  const { api } = useApi();
  const [gymName, setGymName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAddIndividual = async () => {
    setLoading(true);
    try {
      const res = await api.post<{ checkout_url: string }>('/profiles/add-individual', {});
      if (res.success && res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error(res.error || 'Could not start checkout. Please try again.');
      }
    } catch {
      toast.error('Could not start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOwner = async () => {
    if (!gymName.trim() || gymName.trim().length < 2) {
      toast.error('Please enter a gym name (at least 2 characters).');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<{ checkout_url: string }>('/profiles/add-owner', {
        gym_name: gymName.trim(),
      });
      if (res.success && res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error(res.error || 'Could not start checkout. Please try again.');
      }
    } catch {
      toast.error('Could not start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'add_individual') {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            <CardTitle>Train as an individual</CardTitle>
          </div>
          <CardDescription>
            Add a personal Individual subscription on top of your existing account. Both
            subscriptions are billed separately on the same payment method, and your training data
            stays linked to one user.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleAddIndividual} disabled={loading}>
            {loading ? 'Starting checkout…' : 'Add Individual profile'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <CardTitle>Open your own gym</CardTitle>
        </div>
        <CardDescription>
          Add an Owner subscription to launch your own gym alongside your current profile. Your
          existing scores, friends and history stay with you — the new gym is a separate billing
          line on the same Stripe customer.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          placeholder="Gym name"
          value={gymName}
          onChange={(e) => setGymName(e.target.value)}
          maxLength={100}
        />
        <Button onClick={handleAddOwner} disabled={loading}>
          {loading ? 'Starting checkout…' : 'Add Owner profile'}
        </Button>
      </CardContent>
    </Card>
  );
}
